import React from "react";
import Box from "@mui/material/Box";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { fetchClients } from "../../data/Clients";
import { useQuery } from "@tanstack/react-query";
import Filter from "./ClientListFilter";
import LinearProgress from "@mui/material/LinearProgress";
import { IAddress } from "../../data/Address";
import ClientListTable from "./ClientListTable";
import TableToolbar from "../misc/TableToolbar";

export interface IClientFilter {
  search: string;
  sectors: number[];
  address: IAddress | null;
}

const addressFields = [
  "formatted_address",
  "municipality",
  "municipality_subdivision",
  "country_tertiary_subdivision",
  "country_secondary_subdivision",
  "country_subdivision",
  "country",
];

export default function ClientList() {
  document.title = "Clients";

  const { control, reset, handleSubmit } = useForm<IClientFilter>({
    defaultValues: {
      search: "",
      sectors: [],
      address: null,
    },
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const address = React.useMemo(() => {
    const fields = Object.fromEntries(
      addressFields.map((field) => [
        field,
        searchParams.get(field) ?? undefined,
      ])
    );
    const isDefined = Object.values(fields).some((val) => val !== undefined);
    return isDefined ? (fields as IAddress) : null;
  }, [searchParams]);

  const filter = React.useMemo(
    () => ({
      search: searchParams.get("search") ?? "",
      sectors: searchParams.getAll("sectors").map(Number),
      address,
    }),
    [searchParams, address]
  );

  React.useEffect(() => {
    reset(filter);
  }, [filter, reset]);

  const {
    data: clients,
    error,
    isLoading: clientsLoading,
  } = useQuery({
    queryKey: ["clients", searchParams.toString()],
    queryFn: () => fetchClients(searchParams.toString()),
  });

  const handleFilter = (data: IClientFilter) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("search");
      if (data.search) params.set("search", data.search);
      params.delete("sectors");
      data.sectors.forEach((sector) => {
        params.append("sectors", sector.toString());
      });
      addressFields.forEach((field) => {
        params.delete(field);
      });
      if (data.address) {
        const address = data.address as Record<string, string | undefined>;
        addressFields.forEach((field) => {
          const value = address[field];
          if (value) {
            params.set(field, value);
          }
        });
      }
      params.delete("page");
      return params;
    });
  };

  return (
    <Box
      sx={{
        display: "grid",
        borderBottom: 1,
        borderColor: "grey.300",
        gridTemplateColumns: `1fr 320px`,
        gridTemplateAreas: `
        "table filter"
        `,
      }}
    >
      <Box sx={{ gridArea: "table" }}>
        <TableToolbar name="Clients" create="/clients/create" />
        {clientsLoading ? <LinearProgress sx={{ mt: -0.5 }} /> : null}
        <ClientListTable
          clients={clients}
          isLoading={clientsLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
      </Box>
      <Box
        component="form"
        onSubmit={handleSubmit(handleFilter)}
        autoComplete="off"
        sx={{ gridArea: "filter", borderLeft: 1, borderColor: "grey.300" }}
      >
        <Filter control={control} />
      </Box>
    </Box>
  );
}
